/**
 * Eisenhower Process
 * 
 * Implements the Eisenhower Matrix layout: a single view with 4 quadrants in a 2x2 grid.
 * 
 * **Layout Anatomy:**
 * - 1 view containing 4 quadrants arranged in a 2x2 grid
 * - Top-left: Urgent & Important (Do First)
 * - Top-right: Not Urgent & Important (Schedule)
 * - Bottom-left: Urgent & Not Important (Delegate)
 * - Bottom-right: Not Urgent & Not Important (Eliminate)
 * 
 * **Characteristics:**
 * - Priority: NORMAL
 * - Lifecycle: STOP_ON_ALL_VIEWS_CLOSE
 * - Views: 1 view (containing all 4 quadrants)
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';

interface EisenhowerState {
  quadrants: {
    urgentImportant: Array<{ id: string; title: string; description?: string }>;
    notUrgentImportant: Array<{ id: string; title: string; description?: string }>;
    urgentNotImportant: Array<{ id: string; title: string; description?: string }>;
    notUrgentNotImportant: Array<{ id: string; title: string; description?: string }>;
  };
}

export class EisenhowerProcess extends BaseProcess {
  kind: string = 'eisenhower';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'productivity' as const,
    canStop: true,
    preserveState: true,
    timeSensitive: false
  };

  private state: EisenhowerState = {
    quadrants: {
      urgentImportant: [],
      notUrgentImportant: [],
      urgentNotImportant: [],
      notUrgentNotImportant: []
    }
  };

  private eisenhowerView: EisenhowerView | null = null;

  constructor(id: string) {
    super(id);
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[EisenhowerProcess] onStart called for process ${this.id}`);
    
    // Create single view with 4 quadrants
    const view = new EisenhowerView(
      this.id,
      `${this.id}-main`,
      this.state,
      this
    );
    const viewInstance = view.toViewInstance();
    this.eisenhowerView = view;
    
    log.debug(`[EisenhowerProcess] Registering view: ${viewInstance.id}`);
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);

    // Emit process.started
    log.debug(`[EisenhowerProcess] View registered, emitting process.started`);
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  updateState(newState: EisenhowerState): void {
    this.state = newState;
    // Update the single view
    if (this.eisenhowerView) {
      this.eisenhowerView.updateState(this.state);
    }
  }
}

/**
 * Eisenhower View (HTML-based)
 * 
 * Renders the entire Eisenhower matrix as a single view with 4 quadrants in a 2x2 grid.
 */
class EisenhowerView extends BaseViewHTML {
  constructor(
    processId: string,
    viewId: string,
    private state: EisenhowerState,
    private process: EisenhowerProcess
  ) {
    super(processId, viewId, {
      acceptableSizes: [
        { cols: 6, rows: 4 }, // Preferred: 6x4 for comfortable 2x2 quadrant layout
        { cols: 4, rows: 3 },  // Minimum: 4x3
        { cols: 8, rows: 5 }  // Larger option
      ],
      showDismissButton: true,
      priority: 100,
      sizes: [3] // Full height, double width (size 3)
    });
  }

  protected getContentHTML(): string {
    const quadrants = [
      {
        id: 'urgent-important',
        label: 'Urgent & Important',
        items: this.state.quadrants.urgentImportant
      },
      {
        id: 'not-urgent-important',
        label: 'Not Urgent & Important',
        items: this.state.quadrants.notUrgentImportant
      },
      {
        id: 'urgent-not-important',
        label: 'Urgent & Not Important',
        items: this.state.quadrants.urgentNotImportant
      },
      {
        id: 'not-urgent-not-important',
        label: 'Not Urgent & Not Important',
        items: this.state.quadrants.notUrgentNotImportant
      }
    ];
    
    return `
      <div style="
        width: 100%;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr 1fr;
        gap: 12px;
        padding: 16px;
        box-sizing: border-box;
      ">
        ${quadrants.map(quadrant => `
          <div style="
            display: flex;
            flex-direction: column;
            background: rgba(102, 204, 255, 0.05);
            border: 1px solid rgba(102, 204, 255, 0.2);
            border-radius: 8px;
            padding: 12px;
            overflow: hidden;
          ">
            <h3 style="
              margin: 0 0 12px 0;
              font-size: 13px;
              font-weight: 600;
              color: var(--ink, #cfd3dc);
            ">
              ${this.escapeHtml(quadrant.label)}
            </h3>
            <ul style="
              list-style: none;
              padding: 0;
              margin: 0;
              flex: 1;
              overflow-y: auto;
              display: flex;
              flex-direction: column;
              gap: 6px;
            ">
              ${quadrant.items.slice(0, 10).map(item => `
                <li style="
                  padding: 8px;
                  background: rgba(102, 204, 255, 0.1);
                  border: 1px solid rgba(102, 204, 255, 0.3);
                  border-radius: 4px;
                  font-size: 12px;
                  color: var(--ink, #cfd3dc);
                ">
                  ${this.escapeHtml(item.title)}
                </li>
              `).join('')}
              ${quadrant.items.length > 10 ? `
                <li style="
                  padding: 6px;
                  font-size: 11px;
                  color: var(--ink-muted, #6b7280);
                  text-align: center;
                ">
                  +${quadrant.items.length - 10} more
                </li>
              ` : ''}
              ${quadrant.items.length === 0 ? `
                <li style="
                  padding: 8px;
                  font-size: 11px;
                  color: var(--ink-muted, #6b7280);
                  text-align: center;
                  font-style: italic;
                ">
                  Empty
                </li>
              ` : ''}
            </ul>
          </div>
        `).join('')}
      </div>
    `;
  }

  protected render(): void {
    // Call parent to set base styles
    super.render();
    
    // Add view-specific styling
    if (this.element) {
      this.element.style.display = 'flex';
      this.element.style.flexDirection = 'column';
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  updateState(newState: EisenhowerState): void {
    log.debug(`[EisenhowerView] updateState() called for ${this.id}`);
    this.state = newState;
    this.markDirty();
  }
}

registerProcess('eisenhower', (id: string) => new EisenhowerProcess(id));
